import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Lock, Phone, Save, Clock, CheckCheck, MessageCircle } from "lucide-react";
import ClientCodeField, { isValidClientCode } from "./ClientCodeField";
import "./StartPage.css";
import { assessmentDateProblem, inWords, parseDayFirst, toDayFirst } from "../lib/dayFirst";

const LOGO_URL = "/brand/logo-full.png";
const LOGO_MARK_URL = "/brand/logo-mark.png";

export default function StartPage({
  name, setName,
  completedByName, setCompletedByName,
  date, setDate,
  selectedAssessment, setSelectedAssessment,
  preselectedAssessment, preselectedLabel,
  loadingAssessments, assessmentTypes,
  loading, startAssessment,
  clientCode, setClientCode, noCode, setNoCode, codeRequired,
  codePrefilled, onCodeChange,
}) {
  // On desktop, accordion is always open (summary hidden via CSS).
  // On mobile, default to closed so the form is immediately visible.
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 700px)').matches;

  const codeOk = codeRequired
    ? isValidClientCode(clientCode)
    : (noCode || isValidClientCode(clientCode));
  const dateProblem = assessmentDateProblem(date);
  const dateWords = dateProblem ? "" : inWords(parseDayFirst(date));
  const isDisabled = loading || loadingAssessments || !selectedAssessment || !codeOk || !!dateProblem;

  return (
    <div className="sp-page" data-testid="start-page">
      <main className="sp-main">
        <div className="sp-card">
          {/* LEFT PANEL - Welcome */}
          <section className="sp-welcome" aria-label="Before you begin">
            <img
              src={LOGO_MARK_URL}
              alt=""
              aria-hidden="true"
              className="sp-watermark"
            />
            <div className="sp-welcome-top">
              <p className="sp-kicker">Assessment portal</p>
              <h1 className="sp-heading">Before you <br className="sp-dbr" />begin</h1>
              <div className="sp-coral-rule" />

              {/* Mobile quick facts */}
              <div className="sp-facts" aria-hidden="false">
                <span className="sp-fact">
                  <Clock size={15} strokeWidth={2.2} color="#2060E8" />
                  About 5 minutes
                </span>
                <span className="sp-fact">
                  <Save size={15} strokeWidth={2.2} color="#6FA08A" />
                  Saves as you go
                </span>
              </div>

              {/* Accordion: open by default on desktop, collapsible on mobile */}
              <details className="sp-expand" open={!isMobile || undefined}>
                <summary className="sp-summary">
                  What to expect
                </summary>
                <p className="sp-lead">Three things worth knowing before you start:</p>
                <ul className="sp-tips">
                  <li>
                    <CheckIcon />
                    <span>Go with your first response. There are no right or wrong answers.</span>
                  </li>
                  <li>
                    <CheckIcon />
                    <span>Just do your best. If you don't know, or a question feels like a repeat, you don't need to answer every one.</span>
                  </li>
                  <li>
                    <CheckIcon />
                    <span>Your answers save automatically, so nothing is lost even if you don't submit.</span>
                  </li>
                </ul>

                <ol className="sp-steps" aria-label="What happens next">
                  <li className="sp-step-now">
                    <span className="sp-dot sp-dot-active">1</span>
                    <span className="sp-step-label">
                      <b>Your details</b>
                      <span>About a minute</span>
                    </span>
                  </li>
                  <li>
                    <span className="sp-dot">2</span>
                    <span className="sp-step-label">
                      <b>The questions</b>
                      <span>Usually about 5 minutes</span>
                    </span>
                  </li>
                  <li>
                    <span className="sp-dot">3</span>
                    <span className="sp-step-label">
                      <b>Sent to your psychologist</b>
                      <span>Nothing more for you to do</span>
                    </span>
                  </li>
                </ol>

                <p className="sp-assist">
                  <Phone size={15} strokeWidth={2} color="#506080" />
                  <span>
                    Stuck or unsure partway through? Call or text{" "}
                    <a href="tel:0415220944">0415 220 944</a> and we'll help.
                  </span>
                </p>
              </details>
            </div>
          </section>

          {/* RIGHT PANEL - Form or Gateway */}
          {preselectedAssessment ? (
          <section className="sp-form-panel" aria-label="Begin your assessment" data-testid="assessment-selection-card">
            <h2 className="sp-form-heading">Begin your assessment</h2>
            <p className="sp-form-sub">A couple of details, then the questions begin.</p>

            <ClientCodeField
              code={clientCode}
              setCode={setClientCode}
              noCode={noCode}
              setNoCode={setNoCode}
              codeRequired={codeRequired}
              prefilled={codePrefilled}
              onChange={onCodeChange}
            />

            <div className="sp-two-col">
              <div className="sp-field">
                <label htmlFor="sp-date">Assessment date</label>
                <Input
                  id="sp-date"
                  data-testid="date-input"
                  type="text"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  onBlur={() => { if (!dateProblem) { const t = toDayFirst(parseDayFirst(date)); if (t !== date) setDate(t); } }}
                  placeholder="DD/MM/YYYY"
                  inputMode="numeric"
                  aria-invalid={!!dateProblem}
                  aria-describedby="sp-date-note"
                  className="sp-input"
                />
                {dateProblem ? (
                  <p id="sp-date-note" className="sp-date-note sp-date-note--problem" role="alert" data-testid="date-problem">{dateProblem}</p>
                ) : (
                  <p id="sp-date-note" className="sp-date-note" data-testid="date-words">{dateWords}</p>
                )}
              </div>
              <div className="sp-field">
                <label htmlFor="sp-assessment">
                  Assessment
                  <span className="sp-hint"> (pre-selected)</span>
                </label>
                {loadingAssessments ? (
                  <div className="sp-loading">
                    <Loader2 className="animate-spin" size={16} />
                    Loading...
                  </div>
                ) : (
                  <div className="sp-locked" data-testid="locked-assessment">
                    {preselectedLabel || assessmentTypes.find(t => t.value === preselectedAssessment)?.label || preselectedAssessment}
                  </div>
                )}
              </div>
            </div>

            <Button
              onClick={startAssessment}
              disabled={isDisabled}
              className="sp-btn"
              data-testid="start-assessment-button"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={18} />
                  Loading...
                </>
              ) : (
                "Start assessment"
              )}
            </Button>

            <p className="sp-privacy">
              <Lock size={14} strokeWidth={2} color="#506080" />
              <span>Your answers are private. They go only to your psychologist at Clinical Minds and are stored securely.</span>
            </p>
          </section>
          ) : (
          <section className="sp-form-panel sp-gateway" aria-label="Assessment link required" data-testid="gateway-panel">
            <div className="sp-gateway-content">
              <Lock size={32} strokeWidth={1.5} color="#2060E8" />
              <h2 className="sp-form-heading">You need a link to begin</h2>
              <p className="sp-gateway-text">
                To begin an assessment, you'll need a unique link from your psychologist at Clinical Minds.
              </p>
              <p className="sp-gateway-text">
                If you've lost your link or need help, please get in touch:
              </p>
              <div className="sp-gateway-contact">
                <a href="tel:0415220944" className="sp-gateway-action">
                  <Phone size={18} strokeWidth={2} />
                  <span>Call 0415 220 944</span>
                </a>
                <a href="sms:0415220944" className="sp-gateway-action">
                  <MessageCircle size={18} strokeWidth={2} />
                  <span>Text 0415 220 944</span>
                </a>
              </div>
            </div>
          </section>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="sp-footer">
        <span>© 2026 Clinical Minds Psychology</span>
        <span className="sp-footer-sep">·</span>
        <a href="https://www.clinicalminds.net/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        <span className="sp-footer-sep">·</span>
        <a href="https://www.clinicalminds.net/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>
      </footer>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6FA08A" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
